import { useState, useEffect, ChangeEvent, useRef } from "react";
import {
  Paper,
  Grid,
  Box,
  TableBody,
  TableRow,
  TableCell,
  Toolbar,
} from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";

import {
  ButtonSubmit,
  ButtonCancel,
  SearchInput,
  Select,
  Button,
  Loading,
} from "components/shared";
import { RowNoData } from "components";
import { useTable, useRouter, useSet } from "hooks";
import { ISetModulocker } from "config/interfaces";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: {
      paddingBottom: theme.spacing(2),
      borderRadius: "4px",
      background: "var(--backgroundContent)",
      width: "100%",
      "& .MuiToolbar-regular": {
        padding: 0,
      },
    },
    table: {
      borderRadius: "4px",
      background: "var(--backgroundContent)",
      width: "100%",
    },
    margin: {
      margin: theme.spacing(1),
    },
    withoutLabel: {
      marginTop: theme.spacing(3),
    },
    textField: {
      maxWidth: "calc(100% - 12px)",
    },
    input: {
      "& .MuiInputBase-input": {
        boxShadow: "none",
        fontSize: "1.2rem",
        fontWeight: "500",
        textTransform: "uppercase",
      },
    },
    checkbox: {
      "&.MuiCheckbox-colorSecondary.Mui-checked": {
        color: "var(--orange)",
      },
      "& .MuiSvgIcon-root": {
        width: "1.4rem",
      },
    },
  })
);

const filters = [
  {
    label: "STATUS",
    value: "status",
  },
];

interface HeadCell {
  disableSorting?: boolean;
  id: keyof ISetModulocker.TableHead;
  label: string;
}

// TABLE HEAD
const headCells: HeadCell[] = [
  { id: "SNMCM", label: "CÓDIGO" },
  { id: "DSMCM", label: "MODULOCKER" },
  { id: "ENDERECO", label: "LOGRADOURO" },
  { id: "BAIRRO", label: "BAIRRO" },
  { id: "DSCIDADE", label: "CIDADE" },
  { id: "UF", label: "ESTADO" },
  { id: "STATUS", label: "STATUS" },
];

interface DataState {
  filter: string | null;
  status?: string;
}

const initialState: DataState = {
  filter: null,
  status: "0",
};

export default function TableSets() {
  const [data, setData] = useState(initialState);
  const [rows, setRows] = useState([] as ISetModulocker.Set[]);
  const [search, setSearch] = useState("");

  const [filterFn, setFilterFn] = useState({
    fn: (items: any) => {
      return items;
    },
  });
  const { loading, getAll, sets: records } = useSet();
  const router = useRouter();

  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
    useTable({
      records,
      headCells,
      filterFn,
      config: { checkbox: false, actions: true },
    });
  let inputSearchRef = useRef<HTMLInputElement | null>(null);

  const classes = useStyles();

  useEffect(() => {
    (async () => {
      await getAll({ withLoading: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRows(records);
  }, [records]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    let target = e.target;
    setSearch(target.value);
    setFilterFn({
      fn: (items: any) => {
        if (target.value === "") return items;
        else
          return items.filter(
            (x: ISetModulocker.Set) =>
              x.DSMCM.toLowerCase().includes(target.value.toLowerCase()) ||
              String(x.ENDERECO)
                .toLowerCase()
                .includes(target.value.toLowerCase()) ||
              String(x.SNMCM).toLowerCase().includes(target.value.toLowerCase())
          );
      },
    });
  };

  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {};

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;
    const newState = { ...data, [name]: value ? value : checked };
    setData(newState);
  };

  const handleFilterClick = () => {};

  const handleCancelFilter = () => {
    setData({ ...data, filter: null });
    setSearch("");
    getAll();
  };

  return (
    <>
      <Paper className={classes.toolbar} elevation={0}>
        <Toolbar>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={6} md={5}>
              <SearchInput
                ref={inputSearchRef}
                value={search}
                onChange={handleSearch}
                placeholder="POR CÓDIGO / MODULOCKER / ENDEREÇO"
              />
            </Grid>

            <Grid item xs={6} md={6} alignItems="flex-end">
              <Box display="flex" justifyContent="flex-end">
                <Select
                  label="FILTRO"
                  name="filter"
                  // defaultValue="none"
                  choices={filters}
                  value={data.filter}
                  onChange={handleChange}
                />
              </Box>
            </Grid>
          </Grid>
        </Toolbar>

        {data.filter && (
          <Toolbar id="toolbar-period" style={{ marginTop: "16px" }}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item xs={12} md={6}>
                <Paper style={{ padding: "16px" }}>
                  {data.filter === "status" && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <Select
                          label="STATUS"
                          name="status"
                          // choices={statusOptions}
                          value={data.status}
                          onChange={handleChange}
                        />
                      </Grid>
                    </Grid>
                  )}

                  <Box mt={3} display="flex" justifyContent="flex-end">
                    <ButtonCancel
                      onClick={handleCancelFilter}
                      style={{ marginRight: "16px" }}
                    >
                      Cancelar
                    </ButtonCancel>
                    <ButtonSubmit autoFocus onClick={handleFilterClick}>
                      Filtrar
                    </ButtonSubmit>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Toolbar>
        )}
      </Paper>

      {!loading ? (
        <Paper className={classes.table} elevation={0}>
          <TblContainer>
            <TblHead
              numSelected={0}
              rowCount={rows.length}
              onSelectAllClick={handleSelectAllClick}
            />
            <TableBody>
              {recordsAfterPagingAndSorting().length === 0 ? (
                <RowNoData />
              ) : (
                recordsAfterPagingAndSorting().map(
                  (row: ISetModulocker.Set, index: any) => {
                    return (
                      <TableRow hover key={index}>
                        <TableCell align="left">{row.SNMCM}</TableCell>
                        <TableCell align="left">{row.DSMCM}</TableCell>
                        <TableCell align="left">{row.ENDERECO}</TableCell>
                        <TableCell align="left">{row.BAIRRO}</TableCell>
                        <TableCell align="left">{row.DSCIDADE}</TableCell>
                        <TableCell align="left">{row.UF}</TableCell>
                        <TableCell align="left">{row.STATUS}</TableCell>
                        <TableCell align="right">
                          <Button
                            onClick={() =>
                              router.push(`/instalacoes/${row.IDMCM}/status`)
                            }
                          >
                            VER STATUS
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )
              )}
            </TableBody>
          </TblContainer>

          <TblPagination />
        </Paper>
      ) : (
        <Box mt={15} mb={15}>
          <Loading />
        </Box>
      )}
    </>
  );
}
